import React, { useMemo, useState } from 'react'
import { Card, Button, Typography, message, Spin, Tag } from 'antd'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import { PLAN_DISPLAY_NAME } from '../constants/plans'

const Page = styled.div`
  min-height: calc(100vh - 130px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 30px 20px;
  background:
    radial-gradient(circle at 20% 20%, rgba(255, 182, 193, 0.18) 0%, transparent 40%),
    radial-gradient(circle at 80% 80%, rgba(245, 197, 161, 0.18) 0%, transparent 42%),
    #f7f8fb;
`

const Content = styled.div`
  width: 100%;
  max-width: 1200px;

  @media (min-width: 1440px) {
    max-width: 1320px;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;

  @media (min-width: 1440px) {
    grid-template-columns: repeat(2, minmax(480px, 1fr));
    gap: 28px;
  }
`

const StyledCard = styled(Card)`
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(208, 214, 223, 0.8);
  box-shadow: 0 10px 24px rgba(31, 41, 55, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 28px rgba(31, 41, 55, 0.1);
  }

  .ant-card-head {
    background: linear-gradient(135deg, #fff 0%, #f9fafc 100%);
    border-bottom: 1px solid rgba(229, 231, 235, 0.9);
    min-height: 96px;
  }

  .ant-card-head-wrapper {
    align-items: flex-start;
  }

  .ant-card-head-title {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.35;
    white-space: normal;
    color: #1f2937;
    padding-right: 12px;
  }

  .ant-card-body {
    font-size: 24px;
    padding: 26px;
    color: #374151;
  }

  @media (max-width: 820px) {
    .ant-card-head {
      min-height: 78px;
    }
    .ant-card-head-title {
      font-size: 20px;
    }
    .ant-card-body {
      font-size: 18px;
      padding: 18px;
    }
  }
`

const HeaderBlock = styled.div`
  text-align: center;
  margin-bottom: 8px;
`

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 20px;
`

const Label = styled.span`
  color: #6b7280;
  min-width: 64px;
`

const Value = styled.span`
  color: #1f2937;
  font-weight: 600;
`

const PlanTag = styled(Tag)`
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 16px;
`

const roleLabelMap = {
  owner: '店主',
  manager: '經理',
  designer: '設計師',
  receptionist: '櫃台',
  accountant: '會計',
  employee: '員工'
}

// 方案名稱統一由 src/constants/plans.js 提供
const planLabelMap = PLAN_DISPLAY_NAME

const SelectBusinessPage = () => {
  const navigate = useNavigate()
  const { businesses, businessId, switchBusiness } = useAuth()
  const [loadingId, setLoadingId] = useState(null)

  const list = useMemo(
    () => (Array.isArray(businesses) ? businesses : []),
    [businesses]
  )

  const handleSelect = async (targetBusinessId) => {
    if (!targetBusinessId || targetBusinessId === businessId) {
      navigate('/appointments')
      return
    }
    setLoadingId(targetBusinessId)
    try {
      await switchBusiness(targetBusinessId)
      message.success('店家切換成功')
      navigate('/appointments')
    } catch (error) {
      message.error(error?.response?.data?.error || error.message || '切換失敗')
    } finally {
      setLoadingId(null)
    }
  }

  if (!list.length) {
    return (
      <Page>
        <Content>
          <Spin />
        </Content>
      </Page>
    )
  }

  return (
    <Page>
      <Content>
        <Typography.Title level={2} style={{ margin: 0, textAlign: 'center' }}>
          請選擇要操作的店家
        </Typography.Title>
        <Typography.Text
          type='secondary'
          style={{
            display: 'block',
            marginBottom: 24,
            textAlign: 'center',
            fontSize: 24
          }}
        >
          你目前有 {list.length} 家店的權限，選擇後即可進入後台。
        </Typography.Text>

        <Grid>
          {list.map((item) => {
            const active = String(item.businessId) === String(businessId)
            const loading = String(item.businessId) === String(loadingId)
            const roleText = roleLabelMap[item.businessRole] || item.businessRole || '員工'
            const planKey = item.subscription?.plan || 'free'
            const planText = planLabelMap[planKey] || planKey
            return (
              <StyledCard
                key={item.businessId}
                title={item.businessName}
                extra={
                  active ? (
                    <Tag color='processing' style={{ borderRadius: 999, fontSize: 14 }}>
                      目前店家
                    </Tag>
                  ) : null
                }
              >
                <MetaLine>
                  <Label>角色</Label>
                  <Value>{roleText}</Value>
                </MetaLine>
                <MetaLine>
                  <Label>方案</Label>
                  <PlanTag color='magenta'>{planText}</PlanTag>
                </MetaLine>
                <Button
                  type='primary'
                  size='large'
                  block
                  loading={loading}
                  style={{
                    borderRadius: 10,
                    height: 44,
                    fontSize: 24,
                    fontWeight: 600,
                    marginTop: 6
                  }}
                  onClick={() => handleSelect(item.businessId)}
                >
                  {active ? '進入目前店家' : '切換到此店'}
                </Button>
              </StyledCard>
            )
          })}
        </Grid>
      </Content>
    </Page>
  )
}

export default SelectBusinessPage
